import React, { useMemo } from 'react';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { userService } from '../../services';

const ProfileSettingsForm = ({ user, onProfileUpdated }) => {
  const initialValues = useMemo(
    () => ({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
    }),
    [user?.firstName, user?.lastName]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      firstName: Yup.string()
        .trim()
        .min(2, 'First name must be at least 2 characters')
        .required('First name is required'),
      lastName: Yup.string()
        .trim()
        .min(2, 'Last name must be at least 2 characters')
        .required('Last name is required'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
        };

        const response = await userService.updateProfile(payload);
        const message = response?.message || 'Profile updated successfully';
        toast.success(message);

        if (typeof onProfileUpdated === 'function') {
          await onProfileUpdated();
        }
      } catch (error) {
        const validationMessage =
          error.response?.data?.errors?.[0]?.msg ||
          error.response?.data?.errors?.[0]?.message;
        const message =
          validationMessage ||
          error.response?.data?.message ||
          error.message ||
          'Failed to update profile';
        toast.error(message);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box component="form" onSubmit={formik.handleSubmit} noValidate>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
        Update Profile
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
          />
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="flex-end">
        <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
          {formik.isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default React.memo(ProfileSettingsForm);

